"use client"

import { useState, useRef, useEffect } from "react"
import { Check, Plus, XCircle, ChevronDown } from "lucide-react"
import { Input } from "./ui/input"
import { Label } from "./ui/label"

export function HybridSelect({
  label,
  value,
  onChange,
  options,
  error,
  touched,
}: {
  label: string
  value: string
  onChange: (val: string) => void
  options: { label: string; value: string }[]
  error?: string
  touched?: boolean
}) {
  const [inputValue, setInputValue] = useState("")
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setInputValue("") // reset search text when closing
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Filter options based on search input
  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(inputValue.toLowerCase())
  )

  // Resolve the currently selected option's label
  const selectedOption = options.find((o) => o.value === value)

  // Show search text while typing, otherwise show selected label
  const displayValue = open ? inputValue : (selectedOption?.label ?? "") || value || ""

  // Show "Add" action when typed text doesn't match any existing option
  const showCreate =
    inputValue.trim() &&
    !options.some(
      (o) => o.label.toLowerCase() === inputValue.trim().toLowerCase()
    )

  return (
    <div className="space-y-2 relative" ref={containerRef}>
      <Label>{label} <span className="text-red-500">*</span></Label>

      {/* Input */}
      <div className="relative">
        <Input
          value={displayValue}
          placeholder={`Select or type ${label.toLowerCase()}`}
          onChange={(e) => {
            const val = e.target.value
            setInputValue(val)

            // Clear form value when input is emptied
            if (val.trim() === "") {
              onChange("")
            }

            setOpen(true)
          }}
          onFocus={() => {
            setOpen(true)
            setInputValue("") // clear to show all options on focus
          }}
          className="pr-8"
        />

        {/* Clear / Chevron button */}
        {value ? (
          <button
            type="button"
            onClick={() => {
              setInputValue("")
              onChange("")
              setOpen(false)
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
          >
            <XCircle className="h-4 w-4 text-muted-foreground hover:text-foreground" />
          </button>
        ) : (
          <ChevronDown
            className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none"
          />
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-background shadow-md max-h-48 overflow-auto">
          {filtered.length > 0 && (
            <div className="p-1">
              {filtered.slice(0, 10).map((o) => (
                <div
                  key={o.value}
                  onMouseDown={(e) => {
                    e.preventDefault() // prevent input blur
                    onChange(o.value)
                    setInputValue("")
                    setOpen(false)
                  }}
                  className="flex items-center justify-between px-2 py-1.5 text-sm cursor-pointer rounded hover:bg-muted"
                >
                  {o.label}
                  {value === o.value && <Check className="h-4 w-4" />}
                </div>
              ))}
            </div>
          )}

          {/* Create new option */}
          {showCreate && (
            <div
              onMouseDown={(e) => {
                e.preventDefault()
                const newValue = inputValue.trim()
                onChange(newValue)
                setInputValue("")
                setOpen(false)
              }}
              className="flex items-center gap-2 px-2 py-2 text-sm cursor-pointer border-t hover:bg-muted text-primary"
            >
              <Plus className="h-4 w-4" />
              Add &quot;{inputValue}&quot;
            </div>
          )}

          {/* Empty state */}
          {filtered.length === 0 && !showCreate && (
            <div className="px-2 py-2 text-sm text-muted-foreground">
              No results found
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && touched && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  )
}







